const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const rp = (x, y) => ({
  x: Number(x.toFixed(4)),
  y: Number(y.toFixed(4)),
});

const normalize = (vector, fallback = { x: 1, y: 0 }) => {
  const magnitude = Math.hypot(vector.x, vector.y);
  if (magnitude < 1e-8) return fallback;
  return { x: vector.x / magnitude, y: vector.y / magnitude };
};

const midpoint = (a, b) => rp((a.x + b.x) / 2, (a.y + b.y) / 2);

export const normalizeJacketButtonLayout = (value = 'two_buttons') => {
  const normalized = String(value || 'two_buttons').toLowerCase();
  return ['one_button', 'two_buttons', 'three_roll_two'].includes(normalized)
    ? normalized
    : 'two_buttons';
};

export const normalizeJacketFrontClosure = (value = 'single_breasted') => {
  const normalized = String(value || 'single_breasted').toLowerCase();
  return ['single_breasted', 'double_breasted'].includes(normalized)
    ? normalized
    : 'single_breasted';
};

const pointOnLineAtY = (start, end, targetY) => {
  const dy = end.y - start.y;
  if (Math.abs(dy) < 1e-8) return rp(start.x, targetY);
  const t = (targetY - start.y) / dy;
  return rp(start.x + ((end.x - start.x) * t), targetY);
};

const offsetPointTowardInterior = (basePoint, lineStart, lineEnd, offsetDistance, insideHint) => {
  const tangent = normalize({ x: lineEnd.x - lineStart.x, y: lineEnd.y - lineStart.y }, { x: 1, y: 0 });
  const normalA = { x: -tangent.y, y: tangent.x };
  const normalB = { x: tangent.y, y: -tangent.x };
  const candidateA = rp(basePoint.x + (normalA.x * offsetDistance), basePoint.y + (normalA.y * offsetDistance));
  const candidateB = rp(basePoint.x + (normalB.x * offsetDistance), basePoint.y + (normalB.y * offsetDistance));

  if (!insideHint) {
    return { center: candidateA, tangent };
  }

  const distanceToHint = (point) => Math.hypot(point.x - insideHint.x, point.y - insideHint.y);
  return distanceToHint(candidateA) <= distanceToHint(candidateB)
    ? { center: candidateA, tangent }
    : { center: candidateB, tangent };
};

const buildButtonhole = (id, center, _tangent, length) => {
  const halfLength = length / 2;
  return {
    id,
    orientation: 'horizontal',
    start: rp(center.x - halfLength, center.y),
    end: rp(center.x + halfLength, center.y),
  };
};

const resolveButtonCenter = (points, pointName, fallbackCenter) => {
  const override = points?.[pointName];
  if (override && Number.isFinite(Number(override.x)) && Number.isFinite(Number(override.y))) {
    return rp(Number(override.x), Number(override.y));
  }
  return rp(fallbackCenter.x, fallbackCenter.y);
};

export function buildJacketFrontButtons(points = {}, measurements = {}) {
  const CF3 = points.CF3;
  const RL1 = points.RL1;
  const EB = points.EB;
  const LP0_M = points.LP0_M;
  const DB_TOP_BTN = points.DB_TOP_BTN;
  const FWPS = points.FWPS;
  const FBPS = points.FBPS;
  const GP = points.GP;
  const closure = normalizeJacketFrontClosure(measurements?.jacketFrontClosure);
  if (!CF3 || !RL1 || !EB) {
    return {
      closure,
      layout: normalizeJacketButtonLayout(measurements?.jacketButtonLayout),
      buttons: [],
      buttonholes: [],
      buttonholeLength: 0.75,
      lowerButtonDistance: 4.25,
      upperButtonDistance: 4.25,
      edgeOffset: 0.5,
    };
  }

  const layout = normalizeJacketButtonLayout(measurements?.jacketButtonLayout);
  const lowerButtonDistance = clamp(Number(measurements?.jacketLowerButtonDistance ?? 4.25) || 4.25, 0.5, 12);
  const upperButtonDistance = clamp(Number(measurements?.jacketUpperButtonDistance ?? 4.25) || 4.25, 0.5, 12);
  const edgeOffset = clamp(Number(measurements?.jacketButtonEdgeOffset ?? 0.5) || 0.5, 0.125, 2);
  const buttonholeLength = clamp(Number(measurements?.jacketButtonholeLength ?? 0.75) || 0.75, 0.375, 2);

  if (closure === 'double_breasted') {
    const topButtonPoint = DB_TOP_BTN
      ? rp(DB_TOP_BTN.x, DB_TOP_BTN.y)
      : rp(RL1.x - 8, RL1.y - 4.5);
    const buttonSpecs = [
      {
        id: 'jacket-front-button-double-top-single',
        pointName: 'DB_TOP_BTN',
        center: resolveButtonCenter(points, 'DB_TOP_BTN', topButtonPoint),
        tangent: { x: 1, y: 0 },
        kind: 'double_top_single',
      },
      {
        id: 'jacket-front-button-double-upper-edge',
        pointName: 'DB_BTN_UPPER_EDGE',
        center: resolveButtonCenter(points, 'DB_BTN_UPPER_EDGE', rp(RL1.x - edgeOffset, RL1.y)),
        tangent: { x: 1, y: 0 },
        kind: 'double_upper_edge',
      },
      {
        id: 'jacket-front-button-double-upper-inner',
        pointName: 'DB_BTN_UPPER_INNER',
        center: resolveButtonCenter(points, 'DB_BTN_UPPER_INNER', rp(RL1.x - 4, RL1.y)),
        tangent: { x: 1, y: 0 },
        kind: 'double_upper_inner',
      },
      {
        id: 'jacket-front-button-double-lower-edge',
        pointName: 'DB_BTN_LOWER_EDGE',
        center: resolveButtonCenter(points, 'DB_BTN_LOWER_EDGE', rp(RL1.x - edgeOffset, CF3.y + lowerButtonDistance)),
        tangent: { x: 1, y: 0 },
        kind: 'double_lower_edge',
      },
      {
        id: 'jacket-front-button-double-lower-inner',
        pointName: 'DB_BTN_LOWER_INNER',
        center: resolveButtonCenter(points, 'DB_BTN_LOWER_INNER', rp(RL1.x - 4, CF3.y + lowerButtonDistance)),
        tangent: { x: 1, y: 0 },
        kind: 'double_lower_inner',
      },
    ];

    const buttons = buttonSpecs.map((button, index) => ({
      id: button.id,
      x: button.center.x,
      y: button.center.y,
      order: index + 1,
      kind: button.kind,
      pointName: button.pointName,
    }));

    const buttonholes = buttonSpecs.map((button) => buildButtonhole(
      button.id.replace('button', 'buttonhole'),
      button.center,
      button.tangent,
      buttonholeLength,
    ));

    return {
      closure,
      layout: 'double_breasted',
      buttons,
      buttonholes,
      buttonholeLength,
      lowerButtonDistance,
      upperButtonDistance,
      edgeOffset,
      topButtonPoint,
    };
  }

  const lowerInteriorHint = FWPS && FBPS ? midpoint(FWPS, FBPS) : CF3;
  const upperInteriorHint = GP || CF3;
  const frontEdgeTangent = normalize({ x: EB.x - RL1.x, y: EB.y - RL1.y }, { x: 0, y: 1 });

  const buttonSpecs = [
    {
      id: 'jacket-front-button-waist',
      pointName: 'BTN_WAIST',
      center: resolveButtonCenter(points, 'BTN_WAIST', rp(CF3.x, CF3.y)),
      tangent: frontEdgeTangent,
      kind: 'waist',
    },
  ];

  if (layout === 'two_buttons' || layout === 'three_roll_two') {
    const lowerBasePoint = pointOnLineAtY(RL1, EB, CF3.y + lowerButtonDistance);
    const lowerPlacement = offsetPointTowardInterior(lowerBasePoint, RL1, EB, edgeOffset, lowerInteriorHint);
    buttonSpecs.push({
      id: 'jacket-front-button-lower',
      pointName: 'BTN_LOWER',
      center: resolveButtonCenter(points, 'BTN_LOWER', lowerPlacement.center),
      tangent: lowerPlacement.tangent,
      kind: 'lower',
    });
  }

  if (layout === 'three_roll_two' && LP0_M) {
    const upperBasePoint = pointOnLineAtY(RL1, LP0_M, CF3.y - upperButtonDistance);
    const upperPlacement = offsetPointTowardInterior(upperBasePoint, RL1, LP0_M, edgeOffset, upperInteriorHint);
    buttonSpecs.unshift({
      id: 'jacket-front-button-roll',
      pointName: 'BTN_ROLL',
      center: resolveButtonCenter(points, 'BTN_ROLL', upperPlacement.center),
      tangent: upperPlacement.tangent,
      kind: 'roll',
    });
  }

  const buttons = buttonSpecs.map((button, index) => ({
    id: button.id,
    x: button.center.x,
    y: button.center.y,
    order: index + 1,
    kind: button.kind,
    pointName: button.pointName,
  }));

  const buttonholes = buttonSpecs.map((button) => buildButtonhole(
    button.id.replace('button', 'buttonhole'),
    button.center,
    button.tangent,
    buttonholeLength,
  ));

  return {
    closure,
    layout,
    buttons,
    buttonholes,
    buttonholeLength,
    lowerButtonDistance,
    upperButtonDistance,
    edgeOffset,
  };
}